import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Type as BookingType } from '@brighterly/lib-core-types/booking/enums'
import type { LessonTutor, LessonTutorAvailabilityItem, LessonTutorFilters } from './interfaces'

import { useListsStore } from '@shared/model'

import { useTutorsAvailabilityApi } from '../../api/lessons/availability.hook'
import { useLessonPrivileges } from './lesson-privileges.hook'
import {
  buildLessonTutorOptions,
  getLessonTypeCategories,
  isTutorSelectable,
} from './lesson-create'
import { getLessonTutorName } from './lessons'

interface LessonTutorAvailabilityOptions {
  disciplineId: number
  /** 'Y-m-d H:i:s' in the admin view timezone, or '' while not chosen. */
  refStart: ComputedRef<string>
  refType: ComputedRef<BookingType | null>
  refFilters: ComputedRef<LessonTutorFilters | null>
  refTutorId: Ref<number | null>
  /** The lesson being edited or rescheduled: it is left out of the busyness and its type decides the context. */
  bookingId?: number
  /** The lesson's own tutor: always listed, even when the lookup does not return them. */
  currentTutor?: LessonTutor | null
}

interface LessonTutorAvailabilityReturn {
  refOptions: ComputedRef<AppSelectOption<number>[]>
  refTutors: Ref<LessonTutorAvailabilityItem[]>
  refIsLoading: Ref<boolean>
}

/**
 * The tutors offered for a lesson start, read again whenever the start, the lesson type or the applied filters
 * change. A chosen tutor survives a reread while they can still take the lesson.
 */
export const useLessonTutorAvailability = (options: LessonTutorAvailabilityOptions): LessonTutorAvailabilityReturn => {
  const listsStore = useListsStore()
  const { canViewTutorsSchedule } = useLessonPrivileges()

  const refTutors = ref<LessonTutorAvailabilityItem[]>([])
  const refIsLoading = ref<boolean>(false)

  let controller: AbortController | null = null

  const refOptions = computed<AppSelectOption<number>[]>(() => {
    const paidLabels = listsStore.getLists?.tutor.paid_label ?? {}
    const tutorOptions = buildLessonTutorOptions(refTutors.value, paidLabels)
    const current = options.currentTutor

    if (!current || tutorOptions.some(option => option.value === current.id)) return tutorOptions

    return [{ value: current.id, text: getLessonTutorName(current, paidLabels) }, ...tutorOptions]
  })

  const loadTutors = async (): Promise<void> => {
    const start = options.refStart.value
    const type = options.refType.value
    if (!start || !type || !canViewTutorsSchedule.value) return

    const current = new AbortController()
    controller?.abort()
    controller = current

    const filters = options.refFilters.value

    refIsLoading.value = true

    const tutors = await useTutorsAvailabilityApi({
      params: {
        start,
        discipline_id: options.disciplineId,
        booking_id: options.bookingId,
        gender: filters?.gender ?? [],
        special_features: filters?.special_features ?? [],
        accent: filters?.accent ?? [],
        categories: filters?.categories.length ? filters.categories : getLessonTypeCategories(type),
      },
      signal: current.signal,
    })

    // A newer read took over: its answer decides the list and the loader.
    if (current.signal.aborted) return

    refIsLoading.value = false
    if (!tutors) return

    refTutors.value = tutors

    const tutorId = options.refTutorId.value
    const isOwnTutor = tutorId !== null && tutorId === options.currentTutor?.id

    if (tutorId && !isOwnTutor && !isTutorSelectable(tutors, tutorId)) options.refTutorId.value = null
  }

  watch(
    () => [options.refStart.value, options.refType.value, options.refFilters.value],
    () => { void loadTutors() },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    controller?.abort()
  })

  return {
    refOptions,
    refTutors,
    refIsLoading,
  }
}
